import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Message } from "primereact/message";
import { classNames } from "primereact/utils";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Tag } from "primereact/tag";
import { Tooltip } from "primereact/tooltip";
import CalendarIcon from "@/assets/icons/duracao-calendar.svg?react";
import TrashIcon from "@/assets/icons/duracao-trash.svg?react";
import PlusIcon from "@/assets/icons/tipo-plus.svg?react";
import InfoCircleIcon from "@/assets/icons/info-circle.svg?react";
import {
  Calendar,
  DATE_PICKER_LOCALE_FIGMA,
  FormField,
  Illustration,
  SearchMenu,
  TABLE_EMPTY_MESSAGE,
  actionTooltipProps,
} from "@/ds";
import {
  DURACAO_DURACAO_TOOLTIP,
  DURACAO_LABELS,
  DURACAO_PLACEHOLDERS,
  DURACAO_SEM_PERIODO_OBRA_MESSAGE,
  ETAPA_DURACAO_SUBTITLE,
  ETAPA_DURACAO_TITLE,
  LIMITES_CLASSE_VISIVEIS_ETAPAS,
  REQUIRED_FIELDS_NOTE,
} from "../nova-obra.constants";
import { InfoLimitesClasse } from "../components/info-limites-classe";
import { LimitesClasseDialog } from "../components/limites-classe-dialog";
import styles from "./etapa-duracao.module.scss";
import { DURACAO_MAX_DIGITOS, MENU_PERIODOS_BUSCA } from "./etapa-duracao.constants";
import type { EtapaDuracaoViewModel, LinhaView } from "./types-etapa-duracao";

/** Ícone de ajuda com o tooltip da duração em dias corridos (AC16). */
function HintDuracao({ testId }: { readonly testId: string }) {
  return (
    <InfoCircleIcon
      className={styles.hint}
      data-testid={testId}
      aria-label={DURACAO_DURACAO_TOOLTIP}
      role="img"
    />
  );
}

/** Etapa 2 "Duração" (HU 2523): Início da Obra + sequência linear de períodos, estado no wizard. */
export function EtapaDuracaoView({
  errosVisiveis,
  verLimites,
  abrirLimites,
  fecharLimites,
  embarcacaoId,
  dados,
  menu,
  menuAberto,
  alertaPeriodos,
  catalogo,
  informacoesLimites,
  cronograma,
  periodoWizard,
  opcoesMenu,
  mensagemMenuVazio,
  linhas,
  estatisticas,
  alterarDuracao,
  alterarMeta,
  adicionarPeriodo,
  alternarMenu,
  abrirMenu,
  fecharMenu,
  removerPeriodo,
  inicioHabilitado,
  inicioVazio,
  semPeriodoObra,
  semPeriodos,
  mensagemSemPeriodos,
  inicioObraData,
  alterarInicioObra,
}: EtapaDuracaoViewModel) {
  return (
    <div className={styles.body} data-testid="etapa-duracao">
      {alertaPeriodos && (
        <div className={styles.alert} role="alert">
          {alertaPeriodos}
        </div>
      )}

      <header className={styles.header}>
        <h2 className={styles.title}>{ETAPA_DURACAO_TITLE}</h2>
        <p className={styles.subtitle}>{ETAPA_DURACAO_SUBTITLE}</p>
      </header>

      {informacoesLimites && (
        <InfoLimitesClasse
          info={informacoesLimites}
          dados={dados}
          cronograma={cronograma}
          catalogo={catalogo}
          onVerTodos={abrirLimites}
          maxTagsVisiveis={LIMITES_CLASSE_VISIVEIS_ETAPAS}
        />
      )}
      {verLimites && embarcacaoId && (
        <LimitesClasseDialog
          embarcacaoId={embarcacaoId}
          periodo={periodoWizard}
          onClose={fecharLimites}
        />
      )}

      <div className={styles.inicioRow}>
        <FormField.Root
          className={classNames(styles.field, inicioVazio && styles.fieldVazio)}
          error={errosVisiveis.inicioObra}
          required
        >
          <FormField.Label>{DURACAO_LABELS.inicioObra}</FormField.Label>
          <FormField.Control>
            <Calendar
              value={inicioObraData}
              onChange={(event) => alterarInicioObra(event.value)}
              className={styles.inicioData}
              inputClassName={styles.inicioInput}
              panelClassName={styles.painelCalendario}
              // Figma: dias "Dom Seg Ter…" e mês curto (a dev usava a variante `figma` do DatePicker).
              locale={DATE_PICKER_LOCALE_FIGMA}
              pt={{
                // Figma `input-inicio-obra`: o ícone fica dentro da caixa, sem o botão separado do tema.
                dropdownButton: { root: { className: styles.inicioGatilho } },
                day: (opcoes) => ({
                  className: classNames(
                    styles.dia,
                    opcoes?.context.otherMonth && styles.diaOutroMes,
                  ),
                }),
              }}
              placeholder={DURACAO_PLACEHOLDERS.data}
              showOtherMonths
              selectOtherMonths
            />
          </FormField.Control>
          <FormField.Error />
        </FormField.Root>
        <div className={styles.resumo}>
          <p className={styles.required}>{REQUIRED_FIELDS_NOTE}</p>
          <div className={styles.stats}>
            {estatisticas.map(({ label, valor }) => (
              <div key={label} className={styles.stat}>
                <span className={styles.statLabel}>{label}</span>
                <span className={styles.statValue}>{valor}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <section className={styles.periodos} aria-labelledby="duracao-periodos-titulo">
        <div className={styles.topbar}>
          <h3 id="duracao-periodos-titulo" className={styles.periodosTitle}>
            {DURACAO_LABELS.periodosTitle}
          </h3>
          <Button
            type="button"
            size="small"
            severity="secondary"
            icon={<PlusIcon aria-hidden />}
            label={DURACAO_LABELS.adicionarPeriodo}
            disabled={!inicioHabilitado}
            aria-haspopup="menu"
            aria-expanded={menuAberto}
            onClick={alternarMenu}
          />
          {/* Figma `_tieredmenu` 10489:113710: o menu com busca é o `SearchMenu` do DS; a largura é da tela. */}
          <SearchMenu.Root
            ref={menu}
            items={opcoesMenu}
            onSelect={adicionarPeriodo}
            panelClassName={styles.painel}
            onShow={abrirMenu}
            onHide={fecharMenu}
            testId="periodo-menu"
          >
            <SearchMenu.Search aria-label={MENU_PERIODOS_BUSCA} testId="periodo-busca" />
            <SearchMenu.List label={DURACAO_LABELS.colPeriodo} className={styles.painelLista}>
              {opcoesMenu.map(({ id, label, obra, testId }) => (
                <SearchMenu.Item key={id} id={id} testId={testId}>
                  <span className={styles.periodoNome}>{label}</span>
                  {obra && <Tag severity="info" value={DURACAO_LABELS.obra} />}
                </SearchMenu.Item>
              ))}
              <SearchMenu.Empty>{mensagemMenuVazio}</SearchMenu.Empty>
            </SearchMenu.List>
          </SearchMenu.Root>
        </div>

        {semPeriodoObra && (
          <Message
            severity="warn"
            role="status"
            className={classNames("p-message-block", "p-message-sm", styles.aviso)}
            data-testid="aviso-sem-periodo-obra"
            text={DURACAO_SEM_PERIODO_OBRA_MESSAGE}
          />
        )}

        {semPeriodos ? (
          <div className={styles.empty} data-testid="periodos-vazio">
            <p className={styles.emptyText}>{mensagemSemPeriodos}</p>
            <Illustration
              name="sem-equipe"
              size="natural"
              decorative
              testId="etapa-duracao-ilustracao-vazio"
            />
            {errosVisiveis.periodos && (
              <small className={styles.error} role="alert">
                {errosVisiveis.periodos}
              </small>
            )}
          </div>
        ) : (
          <>
            {/* Montado junto da tabela: o ícone do cabeçalho só existe com períodos. */}
            <Tooltip
              key={linhas.length}
              target={`.${styles.hint}`}
              {...actionTooltipProps(DURACAO_DURACAO_TOOLTIP, { className: styles.tooltip })}
            />
            <DataTable
              value={linhas}
              dataKey="uid"
              emptyMessage={TABLE_EMPTY_MESSAGE}
              size="small"
              className={styles.table}
              tableClassName={styles.tabela}
              pt={{ wrapper: { className: styles.tabelaWrapper } }}
              data-testid="periodos-tabela"
            >
              <Column
                header={DURACAO_LABELS.colPeriodo}
                headerClassName={styles.colPeriodo}
                body={(linha: LinhaView) => (
                  <div className={styles.cell}>
                    {/* Nota da PO (HU 2523, prevalece sobre a RN10): o tipo é fixo depois de
                        escolhido; para mudar, remove-se o período e adiciona-se outro. */}
                    <span className={styles.tipoFixo}>
                      <span className={styles.periodoNome} data-testid={linha.tipoTestId}>
                        {linha.tipoRotulo}
                      </span>
                      {linha.tipoObra && <Tag severity="info" value={DURACAO_LABELS.obra} />}
                    </span>
                  </div>
                )}
              />
              <Column
                header={DURACAO_LABELS.colInicio}
                headerClassName={styles.colData}
                body={(linha: LinhaView) => (
                  <div className={styles.cell}>
                    <span className={styles.calculado}>
                      <CalendarIcon aria-hidden />
                      <InputText
                        disabled
                        readOnly
                        value={linha.inicioTexto}
                        placeholder={DURACAO_PLACEHOLDERS.data}
                        aria-label={linha.inicioAria}
                        className={styles.input}
                      />
                    </span>
                  </div>
                )}
              />
              <Column
                header={
                  <span className={styles.headerComHint}>
                    {DURACAO_LABELS.colDuracao}
                    <HintDuracao testId="hint-duracao" />
                  </span>
                }
                headerClassName={styles.colDuracao}
                body={(linha: LinhaView) => (
                  <FormField.Root className={styles.cell} error={linha.erroDuracao}>
                    <FormField.Control>
                      <InputText
                        inputMode="numeric"
                        maxLength={DURACAO_MAX_DIGITOS}
                        value={linha.duracaoTexto}
                        placeholder={DURACAO_PLACEHOLDERS.duracao}
                        aria-label={linha.duracaoAria}
                        onChange={(event) => alterarDuracao(linha.uid, event.target.value)}
                        className={classNames(styles.input, styles.numero)}
                      />
                    </FormField.Control>
                    <FormField.Error />
                  </FormField.Root>
                )}
              />
              <Column
                header={DURACAO_LABELS.colFim}
                headerClassName={styles.colData}
                body={(linha: LinhaView) => (
                  <div className={styles.cell}>
                    <span className={styles.calculado}>
                      <CalendarIcon aria-hidden />
                      <InputText
                        disabled
                        readOnly
                        value={linha.fimTexto}
                        placeholder={DURACAO_PLACEHOLDERS.fim}
                        aria-label={linha.fimAria}
                        className={styles.input}
                      />
                    </span>
                  </div>
                )}
              />
              <Column
                header={DURACAO_LABELS.colMeta}
                headerClassName={styles.colDias}
                body={(linha: LinhaView) => (
                  <div className={styles.cell}>
                    <InputText
                      inputMode="numeric"
                      maxLength={DURACAO_MAX_DIGITOS}
                      value={linha.metaTexto}
                      aria-label={linha.metaAria}
                      onChange={(event) => alterarMeta(linha.uid, event.target.value)}
                      className={classNames(styles.input, styles.numero)}
                    />
                  </div>
                )}
              />
              <Column
                headerClassName={styles.colAcoes}
                bodyClassName={styles.tdAcoes}
                body={(linha: LinhaView) => (
                  <Button
                    type="button"
                    icon={<TrashIcon aria-hidden />}
                    rounded
                    text
                    size="small"
                    className={styles.remover}
                    aria-label={linha.removerAria}
                    onClick={() => removerPeriodo(linha, linha.indice)}
                  />
                )}
              />
            </DataTable>
          </>
        )}
      </section>
    </div>
  );
}
